import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Youtube,
  Share2,
  MessageCircle,
  Mail,
  Link2,
  X as XIconClose,
  Check,
} from "lucide-react";

const JOIN_CLASS_URL = "https://us06web.zoom.us/j/2480049812";
const JOIN_COMMUNITY_URL = "https://shyftcoalition.com/sign_up";

/* ------------- Holiday skip ------------- */
const HOLIDAY = {
  enabled: true,
  reason: "Scheduled Break",
  startY: 2026, startM: 9, startD: 14, startH: 0,
  endY:   2026, endM:   10, endD:   5, endH:   0,
  noteShort: "No classes this week — back Monday, October 5 at 8pm ET",
};
function holidayWindow() {
  if (!HOLIDAY.enabled) return null;
  const start = etWallClockToInstant(HOLIDAY.startY, HOLIDAY.startM, HOLIDAY.startD, HOLIDAY.startH);
  const end = etWallClockToInstant(HOLIDAY.endY, HOLIDAY.endM, HOLIDAY.endD, HOLIDAY.endH);
  return { start, end };
}
function isWithinHoliday(date) {
  const w = holidayWindow();
  if (!w) return false;
  return date >= w.start && date < w.end;
}
function holidayIsActive(now = new Date()) {
  const w = holidayWindow();
  if (!w) return false;
  return now < w.end;
}
/* ------------- end Holiday ------------- */

/* ------------- One-time Special Event override ------------- */
const SPECIAL_EVENT = {
  enabled: true,
  title: "SHYFT Coalition Launch 300",
  shortLabel: "SHYFT Coalition Launch 300",
  startY: 2026, startM: 6, startD: 13, startH: 13,
  endY: 2026, endM: 6, endD: 13, endH: 18,
  flyer: "/flyer-launch-300.jpg",
  zoom: "https://us06web.zoom.us/j/2480049812",
};
function getSpecialEvent(now = new Date()) {
  if (!SPECIAL_EVENT.enabled) return null;
  const start = etWallClockToInstant(
    SPECIAL_EVENT.startY, SPECIAL_EVENT.startM, SPECIAL_EVENT.startD, SPECIAL_EVENT.startH
  );
  const end = etWallClockToInstant(
    SPECIAL_EVENT.endY, SPECIAL_EVENT.endM, SPECIAL_EVENT.endD, SPECIAL_EVENT.endH
  );
  if (now >= end) return null;
  return {
    active: now >= start && now < end,
    when: start,
    endsAt: end,
    label: SPECIAL_EVENT.shortLabel,
    title: SPECIAL_EVENT.title,
    flyer: SPECIAL_EVENT.flyer,
    zoom: SPECIAL_EVENT.zoom,
    isSpecial: true,
  };
}
/* ------------- end Special Event ------------- */

/* ------------- Timezone helpers (auto-detection) ------------- */
const ET_TZ = "America/New_York";

function etWallClockToInstant(year, month, day, hour) {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, 0, 0));
  const etHourStr = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ,
    hour12: false,
    hour: "2-digit",
  })
    .formatToParts(utcGuess)
    .find((p) => p.type === "hour").value;
  const etHour = parseInt(etHourStr, 10);
  const offsetHours = hour - etHour;
  return new Date(utcGuess.getTime() + offsetHours * 3600 * 1000);
}

function etDateParts(d) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

function getNextClass(now = new Date()) {
  const special = getSpecialEvent(now);
  if (special) return special;

  const dayLabelMap = {
    Sun: "Charting Class",
    Mon: "SHYFT Class",
    Tue: "SmartLock™ Class",
    Wed: "EazyBot Class",
    Thu: "EazyBot Affiliate Class",
  };
  let active = null;
  let next = null;

  for (let i = -1; i < 35; i++) {
    const probe = new Date(now.getTime() + i * 86400 * 1000);
    const p = etDateParts(probe);
    const y = parseInt(p.year, 10);
    const m = parseInt(p.month, 10);
    const d = parseInt(p.day, 10);
    const wk = p.weekday;

    if (["Sun", "Mon", "Tue", "Wed", "Thu"].includes(wk)) {
      const start = etWallClockToInstant(y, m, d, 20);
      const end = etWallClockToInstant(y, m, d, 21);
      if (isWithinHoliday(start)) {
        // skip — class is on holiday
      } else {
        if (!active && now >= start && now < end) {
          active = {
            active: true,
            when: start,
            endsAt: end,
            label: dayLabelMap[wk],
          };
        }
        if (now < start && (!next || start < next.when)) {
          next = { active: false, when: start, label: dayLabelMap[wk] };
        }
      }
    }
  }
  return active || next;
}

function detectActiveZone() {
  let tz = "UTC";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_) {}
  const eastern = new Set([
    "America/New_York", "America/Detroit", "America/Indiana/Indianapolis",
    "America/Toronto", "America/Kentucky/Louisville",
  ]);
  const central = new Set([
    "America/Chicago", "America/Mexico_City", "America/Winnipeg",
    "America/Indiana/Knox",
  ]);
  const mountain = new Set([
    "America/Denver", "America/Phoenix", "America/Edmonton",
    "America/Boise", "America/Cambridge_Bay",
  ]);
  const pacific = new Set([
    "America/Los_Angeles", "America/Vancouver", "America/Tijuana",
  ]);
  if (eastern.has(tz)) return { zone: "ET", iana: tz };
  if (central.has(tz)) return { zone: "CT", iana: tz };
  if (mountain.has(tz)) return { zone: "MT", iana: tz };
  if (pacific.has(tz)) return { zone: "PT", iana: tz };
  return { zone: null, iana: tz };
}

function shortZoneName(iana) {
  const map = {
    "America/New_York": "ET", "America/Detroit": "ET",
    "America/Indiana/Indianapolis": "ET", "America/Toronto": "ET",
    "America/Kentucky/Louisville": "ET", "America/Chicago": "CT",
    "America/Mexico_City": "CT", "America/Winnipeg": "CT",
    "America/Indiana/Knox": "CT", "America/Denver": "MT",
    "America/Phoenix": "MT", "America/Edmonton": "MT",
    "America/Boise": "MT", "America/Cambridge_Bay": "MT",
    "America/Los_Angeles": "PT", "America/Vancouver": "PT",
    "America/Tijuana": "PT",
  };
  return map[iana] || null;
}

function formatLocal(when, iana) {
  const short = shortZoneName(iana);
  try {
    if (short) {
      const dayPart = new Intl.DateTimeFormat("en-US", {
        timeZone: iana, weekday: "long", month: "long", day: "numeric",
      }).format(when);
      const timePart = new Intl.DateTimeFormat("en-US", {
        timeZone: iana, hour: "numeric", minute: "2-digit",
      }).format(when);
      return `${dayPart} @ ${timePart} ${short}`;
    }
    const dayPart = new Intl.DateTimeFormat("en-US", {
      timeZone: iana, weekday: "long", month: "long", day: "numeric",
    }).format(when);
    const timePart = new Intl.DateTimeFormat("en-US", {
      timeZone: iana, hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(when);
    return `${dayPart} @ ${timePart}`;
  } catch (_) {
    return when.toLocaleString();
  }
}

function formatClassDate(when) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ,
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(when);
}

function buildTzState() {
  const detected = detectActiveZone();
  const nxt = getNextClass();
  let nextClassHeading = null;
  let nextClassDate = null;
  if (nxt) {
    nextClassHeading = nxt.active
      ? `Class in Session: ${nxt.label} — Join Now`
      : `Next Class: ${nxt.label}`;
    nextClassDate = formatClassDate(nxt.when);
  }
  return { tz: detected, nxt, nextClassHeading, nextClassDate };
}
/* ------------- end timezone helpers ------------- */

const SOCIALS = [
  { label: "YouTube", href: "https://www.youtube.com/@ShyftCoalition", testid: "social-youtube", Icon: Youtube },
];

const ROWS = [
  {
    key: "monday",
    day: "Mondays",
    dayNote: "@ 8pm ET",
    icon: "/icons/monday.png",
    rsvp: "https://shyftcoalition.com/spaces/8975330/events",
    brand: "SHYFT",
    titleSuffix: "Classes",
    tagline: "Learn to 'Shift How You Financially Think'",
    body: <>Each week begins with an introduction of the SHYFT Coalition<sup className="shyft-tm-dark">TM</sup> and an overview of the SHYFT Financial Ecosystem. If you're new to the SHYFT Coalition, these Classes are the perfect place to begin your journey.</>,
  },
  {
    key: "tuesday",
    day: "Tuesdays",
    dayNote: "@ 8pm ET",
    icon: "/icons/smartlock_logo.png",
    rsvp: "https://shyftcoalition.com/spaces/20736990/events",
    brand: "SmartLock",
    brandTM: true,
    titleSuffix: "Classes",
    tagline: "The Home of 'Guaranteed Growth'",
    body: "Imagine having access to a financial tool that allows you to continuously earn interest and dividends, for the rest of your life, on money you're spending today. These Classes reveal the extraordinary power of SmartLocks and why they're a cornerstone of the SHYFT Financial Ecosystem.",
  },
  {
    key: "wednesday",
    day: "Wednesdays",
    dayNote: "@ 8pm ET",
    icon: "/icons/eazybot.png",
    rsvp: "https://shyftcoalition.com/spaces/8957417/events",
    brand: "EazyBot",
    titleSuffix: "Classes",
    tagline: "Autopilot Your Income",
    body: "Trading bots are software programs designed to read different financial markets and execute trades. These powerful financial tools have traditionally been reserved for banks and accredited investors. Wednesday's Classes explain how EazyBot helps you put these 'digital employees' to work for you and your family.",
  },
  {
    key: "thursday",
    day: "Thursdays",
    dayNote: "@ 8pm ET",
    icon: "/icons/eazybot.png",
    rsvp: "https://shyftcoalition.com/spaces/11095065/events",
    brand: "EazyBot",
    titleSuffix: "Affiliate Classes",
    tagline: "EVERYONE Needs Trading Bots!",
    body: "EazyBot realized their users were constantly encouraging family and friends to get digital employees, so they created an optional Affiliate Program that pays users to do what they were already doing for free. These Classes outline the details of this highly lucrative opportunity and explains why EVERYONE Needs Trading Bots!",
  },
  {
    key: "sunday",
    day: "Sundays",
    dayNote: "@ 8pm ET",
    icon: "/icons/thursday.png",
    rsvp: "https://shyftcoalition.com/spaces/11095065/events",
    brand: "Charting",
    titleSuffix: "Classes",
    tagline: "A 'Cheat Code' for Passive Income",
    body: "Learning how to chart assets isn't just a 'cheat code' for passive income, it positions you to make well-informed everyday financial decisions for your family. Our proprietary training indicators are engineered to help you easily see the hidden trends and patterns, keeping these Classes engaging and the information easy to understand.",
  },
];

const ScheduleRow = ({ row, delay }) => (
  <div
    className="shyft-fade-up shyft-row-hover grid grid-cols-12 gap-6 lg:gap-10 items-center py-8 lg:py-12"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`schedule-row-${row.key}`}
  >
    <div className="col-span-12 sm:col-span-2 flex flex-col items-center sm:justify-center">
      <a
        href={row.rsvp}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`RSVP for ${row.day} class`}
        data-testid={`row-icon-link-${row.key}`}
        className="group shyft-row-icon w-28 h-28 rounded-full overflow-hidden flex items-center justify-center"
      >
        <img
          src={row.icon}
          alt={`${row.day} icon`}
          className="shyft-row-icon-image w-[88%] h-[88%] object-contain"
          data-testid={`row-icon-image-${row.key}`}
          loading="lazy"
        />
      </a>
      <a
        href={row.rsvp}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`RSVP for ${row.day} class`}
        data-testid={`row-click-to-rsvp-${row.key}`}
        className="mt-2 text-[0.68rem] tracking-[0.06em] italic font-serif-soft hover:opacity-70 transition-opacity"
        style={{ color: "var(--shyft-caramel)", fontWeight: 600 }}
      >
        Click to RSVP
      </a>
    </div>

    <div className="col-span-12 sm:col-span-3">
      <div
        className="font-display leading-none text-[#1A0F05]"
        style={{ fontSize: "clamp(1.8rem, 3vw, 2.75rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
      >
        {row.day}
      </div>
      {row.dayLine2 && (
        <div
          className="font-display leading-none text-[#1A0F05]"
          style={{ fontSize: "clamp(1.8rem, 3vw, 2.75rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
        >
          {row.dayLine2}
        </div>
      )}
      {row.dayNote && (
        <div
          className="font-serif-soft italic mt-2 text-base lg:text-lg"
          style={{ color: "var(--shyft-caramel)", fontWeight: 500 }}
        >
          {row.dayNote}
        </div>
      )}
    </div>

    <div className="col-span-12 sm:col-span-7">
      <h3
        className="font-display text-lg lg:text-xl mb-3 text-[#2C1A0E]"
        data-testid={`row-title-${row.key}`}
      >
        {row.brand === "SHYFT" ? (
          <span className="shyft-gold-gradient-text">{row.brand}</span>
        ) : (
          <span className="text-[#2C1A0E]">{row.brand}</span>
        )}
        {row.brandTM && <sup className="shyft-tm-dark">TM</sup>}
        {row.titleSuffix && (
          <span className="text-[#2C1A0E]"> {row.titleSuffix}</span>
        )}
        {row.tagline && (
          <>
            <span className="mx-2" style={{ color: "rgba(142,109,59,0.45)" }}>–</span>
            <span
              className="text-[#5C3D1E]"
              style={{ fontStyle: "italic", fontWeight: 400 }}
            >
              {row.tagline}
            </span>
          </>
        )}
      </h3>
      {row.body && (
        <p className="text-[0.95rem] lg:text-base leading-[1.65] text-[#4A3222] font-serif-soft">
          {row.body}
        </p>
      )}
    </div>
  </div>
);

/* ----------------- Share Modal ----------------- */
function ShareModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const message =
    "Join me at a free SHYFT Coalition Class — it's time to shift how you financially think.";

  const copy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(pageUrl);
      } else {
        throw new Error("clipboard-api-unavailable");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = pageUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (err) {
        console.error("copy failed", err);
      }
    }
  }, [pageUrl]);

  if (!open) return null;

  const opts = [
    {
      label: "Text Message",
      href: `sms:?&body=${encodeURIComponent(`${message} ${pageUrl}`)}`,
      Icon: MessageCircle,
      testid: "share-sms",
    },
    {
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(`${message} ${pageUrl}`)}`,
      external: true,
      Icon: WhatsAppIcon,
      testid: "share-whatsapp",
    },
    {
      label: "Email",
      href: `mailto:?subject=${encodeURIComponent(
        "SHYFT Coalition Classes"
      )}&body=${encodeURIComponent(`${message}\n\n${pageUrl}`)}`,
      Icon: Mail,
      testid: "share-email",
    },
  ];

  return (
    <div
      className="shyft-share-backdrop fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:p-6 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-modal-backdrop"
    >
      <div
        className="shyft-share-modal w-full sm:max-w-md rounded-lg p-7 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close share dialog"
          className="shyft-share-close absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center transition-colors duration-200"
          data-testid="share-close-button"
        >
          <XIconClose size={16} />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <span className="shyft-dot" />
          <span
            className="text-[0.72rem] tracking-[0.28em]"
            style={{ color: "var(--shyft-caramel)" }}
          >
            SHARE
          </span>
        </div>

        <h3 className="font-display text-2xl text-[#24170D] mb-1" data-testid="share-modal-title">
          Invite friends &amp; family to a Class
        </h3>
        <p className="text-[#6F543C] text-sm mb-6" data-testid="share-modal-description">
          Share the SHYFT Class Schedule via your favorite app.
        </p>

        <div className="grid grid-cols-3 gap-3 mb-5">
          {opts.map(({ label, href, Icon, testid, external }) => (
            <a
              key={label}
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              data-testid={testid}
              className="shyft-share-option flex flex-col items-center gap-2 py-4 rounded-md transition-[transform,border-color,background-color] duration-200 hover:-translate-y-0.5"
            >
              <Icon size={22} style={{ color: "var(--shyft-caramel)" }} />
              <span className="text-xs tracking-wide">{label}</span>
            </a>
          ))}
        </div>

        <button
          onClick={copy}
          data-testid="share-copy-link"
          className="shyft-share-copy w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md transition-colors duration-200 overflow-hidden"
        >
          <span className="flex flex-1 min-w-0 items-center gap-3 text-sm">
            {copied ? (
              <Check size={18} style={{ color: "var(--shyft-caramel)" }} />
            ) : (
              <Link2 size={18} style={{ color: "var(--shyft-caramel)" }} />
            )}
            <span className="truncate min-w-0">
              {copied ? "Link copied!" : pageUrl}
            </span>
          </span>
          <span
            className="flex-shrink-0 text-[0.7rem] tracking-[0.22em]"
            style={{ color: "var(--shyft-caramel)" }}
          >
            {copied ? "COPIED" : "COPY"}
          </span>
        </button>
      </div>
    </div>
  );
}

/* ----------------- Landing ----------------- */
const Landing = () => {
  const [shareOpen, setShareOpen] = useState(false);
  const initialState = useMemo(() => buildTzState(), []);
  const {
    tz,
    nextClassHeading: initialHeading,
    nextClassDate: initialDate,
  } = initialState;
  const [nextClass, setNextClass] = useState(() => initialState.nxt);

  const recomputeNextClass = useCallback(() => {
    const fresh = buildTzState();
    setNextClass(fresh.nxt);
  }, []);

  const nextClassHeading = useMemo(() => {
    if (!nextClass) return initialHeading;
    return nextClass.active
      ? `Class in Session: ${nextClass.label} — Join Now`
      : `Next Class: ${nextClass.label}`;
  }, [nextClass, initialHeading]);

  const nextClassDate = useMemo(() => {
    if (!nextClass) return initialDate;
    return formatClassDate(nextClass.when);
  }, [nextClass, initialDate]);

  const openZoom = () => window.open(JOIN_CLASS_URL, "_blank", "noopener,noreferrer");
  const openCommunity = () =>
    window.open(JOIN_COMMUNITY_URL, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen w-full overflow-x-hidden" data-testid="landing-page">
      {/* ============ TOP BAR — fixed ============ */}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10"
        data-testid="top-bar"
      >
        <nav
          className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4"
          data-testid="top-nav"
        >
          <a href="/" className="flex items-center gap-3" data-testid="brand-link">
            <img
              src="/icons/sc_logo.png"
              alt="SHYFT Coalition"
              className="w-11 h-11 object-contain"
            />
            <span
              className="font-display whitespace-nowrap"
              style={{ fontWeight: 600, letterSpacing: "0.08em", fontSize: "1.05rem" }}
              data-testid="header-brand-name"
            >
              <span className="text-white">SHYFT</span>{" "}
              <span className="shyft-gold-gradient-text">Coalition<sup className="shyft-tm-gold">TM</sup></span>
            </span>
          </a>

          <button
            onClick={openCommunity}
            className="shyft-cta-pill inline-flex items-center gap-2 px-5 py-2.5 text-xs sm:text-sm font-semibold"
            data-testid="nav-join-community-button"
          >
            <span className="shyft-dot" />
            Join Our Community
          </button>
        </nav>
      </header>

      {/* ============ SCHEDULE ============ */}
      <main id="schedule" className="shyft-ivory-bg relative pt-[76px]" data-testid="schedule-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="text-center mb-10">
            <div className="shyft-rule-gold mx-auto mb-8" />
            <h2
              className="font-display text-[#1A0F05] text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              data-testid="schedule-title"
              style={{ fontWeight: 800, letterSpacing: "-0.01em" }}
            >
              <span className="shyft-hero-title-gold">SHYFT</span>
              <span style={{ color: "#1A0F05" }}> Class Schedule</span>
            </h2>

            <p
              className="font-display italic text-xl lg:text-2xl mt-5 shyft-tagline-gold"
              data-testid="schedule-tagline"
            >
              It's Time to Shift How You Financially Think
            </p>

            <div className="shyft-divider w-40 mx-auto mt-10 mb-8" />

            <section className="shyft-next-class-card mx-auto" data-testid="next-class-card">
              {nextClassHeading && (
                <p
                  className="shyft-next-class-line shyft-next-class-heading flex items-center justify-center gap-3 flex-wrap"
                  data-testid="next-class-local"
                >
                  {nextClass && nextClass.active && (
                    <span className="shyft-live-badge" data-testid="live-badge">
                      <span className="shyft-live-dot" />
                      LIVE
                    </span>
                  )}
                  <span className="shyft-next-class-heading-copy">
                    <span className="block">{nextClassHeading}</span>
                    {nextClassDate && (
                      <span className="block" data-testid="next-class-date">
                        {nextClassDate}
                      </span>
                    )}
                  </span>
                </p>
              )}

              {nextClass && (
                <Countdown
                  key={
                    (nextClass.active ? "live-" : "next-") +
                    (nextClass.active ? nextClass.endsAt.getTime() : nextClass.when.getTime())
                  }
                  targetDate={nextClass.active ? nextClass.endsAt : nextClass.when}
                  prefix={nextClass.active ? "Ends in" : "Starts in"}
                  onExpired={recomputeNextClass}
                />
              )}

              <div
                className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-7 sm:mt-8"
                data-testid="schedule-time-pills"
              >
                <span className="font-serif-soft italic text-sm text-[#4A3222]">
                  Your Local Time
                </span>
                <TimePill label="8pm ET" active={tz.zone === "ET"} testid="time-pill-et" />
                <TimePill label="7pm CT" active={tz.zone === "CT"} testid="time-pill-ct" />
                <TimePill label="6pm MT" active={tz.zone === "MT"} testid="time-pill-mt" />
                <TimePill label="5pm PT" active={tz.zone === "PT"} testid="time-pill-pt" />
              </div>
            </section>

            {holidayIsActive() && <HolidayNotice />}

            <div
              className="flex flex-wrap items-center justify-center gap-4 mt-8"
              data-testid="schedule-cta-row"
            >
              <button
                onClick={openZoom}
                className="shyft-cta-pill inline-flex items-center gap-2.5 px-7 py-3 text-[0.78rem] font-semibold tracking-[0.22em]"
                data-testid="schedule-join-class-button"
              >
                <span className="shyft-dot" />
                JOIN CLASS
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[0.78rem] tracking-[0.22em] text-[#4A3222] hover:text-[#2C1A0E] border border-[rgba(180,130,40,0.40)] hover:border-[rgba(212,160,48,0.80)] hover:shadow-[0_0_12px_rgba(212,160,48,0.25)] transition-all duration-300"
                data-testid="schedule-share-button"
              >
                <Share2 size={14} />
                SHARE
              </button>
            </div>
          </div>

          <div className="shyft-divider mt-10 mb-2" />

          {nextClass && nextClass.isSpecial && (
            <SpecialEventBanner event={nextClass} joinUrl={JOIN_CLASS_URL} tz={tz} />
          )}

          {ROWS.map((r, i) => (
            <React.Fragment key={r.key}>
              <ScheduleRow row={r} delay={80 + i * 80} />
              <div className="shyft-divider" />
            </React.Fragment>
          ))}

          {/* Bottom CTA */}
          <div
            className="flex flex-col items-center text-center mt-20 lg:mt-24"
            data-testid="cta-block"
          >
            <div className="flex flex-wrap gap-6 justify-center items-center">
              <button
                onClick={openZoom}
                className="shyft-cta-pill inline-flex items-center gap-2.5 px-8 py-3.5 text-[0.82rem] font-semibold tracking-[0.22em]"
                data-testid="footer-join-class-button"
              >
                <span className="shyft-dot" />
                JOIN CLASS
              </button>
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[0.82rem] tracking-[0.22em] text-[#4A3222] hover:text-[#2C1A0E] border border-[rgba(180,130,40,0.40)] hover:border-[rgba(212,160,48,0.80)] hover:shadow-[0_0_12px_rgba(212,160,48,0.25)] transition-all duration-300"
                data-testid="footer-share-button"
              >
                <Share2 size={16} />
                SHARE
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* ============ FOOTER ============ */}
      <footer className="shyft-hero-bg border-t border-white/8" data-testid="footer">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/icons/sc_logo.png"
                alt="SHYFT Coalition"
                className="w-9 h-9 object-contain"
              />
              <span
                className="font-display whitespace-nowrap"
                style={{ fontWeight: 600, letterSpacing: "0.08em", fontSize: "0.95rem" }}
                data-testid="footer-brand-name"
              >
                <span className="text-white">SHYFT</span>{" "}
                <span className="shyft-gold-gradient-text">Coalition<sup className="shyft-tm-gold">TM</sup></span>
              </span>
            </div>

            <p className="font-serif-soft italic text-white/60 text-sm max-w-md">
              Shift How You Financially Think
            </p>

            <div className="flex items-center gap-3" data-testid="social-links">
              {SOCIALS.map(({ label, href, testid, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={testid}
                  className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-white/70 hover:text-[var(--shyft-gold-bright)] transition-all duration-300 border border-white/10 hover:border-[rgba(201,169,110,0.55)] hover:-translate-y-0.5"
                >
                  <Icon size={18} />
                  {label === "YouTube" && (
                    <span className="text-xs font-semibold tracking-[0.14em]">
                      Subscribe
                    </span>
                  )}
                </a>
              ))}
            </div>

            <div className="shyft-divider w-32 my-2" />

            <p className="text-white/40 text-xs tracking-wider">
              © {new Date().getFullYear()} SHYFT Coalition · All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
};

/* ----------------- Sub-components ----------------- */
function HolidayNotice() {
  return (
    <div
      className="mt-7 mx-auto max-w-2xl px-5 py-4 rounded-2xl flex items-center justify-center gap-3 text-center"
      style={{
        background: "rgba(44, 26, 14, 0.04)",
        border: "1px solid rgba(255, 77, 77, 0.40)",
        boxShadow: "0 0 0 1px rgba(255,77,77,0.10), 0 10px 40px -15px rgba(255,77,77,0.18)",
      }}
      data-testid="holiday-notice"
    >
      <span
        aria-hidden="true"
        className="text-2xl leading-none"
        style={{ filter: "drop-shadow(0 0 8px rgba(255,77,77,0.5))" }}
      >
        ★
      </span>
      <p className="text-sm lg:text-base text-[#2C1A0E] leading-snug">
        <span className="font-semibold" style={{ color: "#c0392b" }}>
          Scheduled break —
        </span>{" "}
        There will be no classes this week.
        <br className="hidden sm:block" />
        Classes resume{" "}
        <span className="text-[#2C1A0E] font-semibold">
          Monday, October 5 at 8pm ET
        </span>
        .
      </p>
      <span
        aria-hidden="true"
        className="text-2xl leading-none"
        style={{ filter: "drop-shadow(0 0 8px rgba(0,184,255,0.5))" }}
      >
        ★
      </span>
    </div>
  );
}

function ShyftAcronymTagline() {
  return (
    <>
      <span className="shyft-gold-gradient-text">S</span>hift{" "}
      <span className="shyft-gold-gradient-text">H</span>ow{" "}
      <span className="shyft-gold-gradient-text">Y</span>ou{" "}
      <span className="shyft-gold-gradient-text">F</span>inancially{" "}
      <span className="shyft-gold-gradient-text">T</span>hink
    </>
  );
}

function SpecialEventBanner({ event, joinUrl, tz }) {
  const localStr = formatLocal(event.when, tz?.iana || "America/New_York");
  return (
    <section
      className="shyft-special-banner mt-10 mb-4 overflow-hidden rounded-3xl"
      data-testid="special-event-banner"
    >
      <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
        <a
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="special-event-flyer-link"
          className="md:col-span-2 block relative group"
          aria-label="Open Zoom for SHYFT Coalition Launch 300"
        >
          <img
            src={event.flyer}
            alt="SHYFT Coalition Launch 300 flyer"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
            loading="eager"
          />
        </a>

        <div className="md:col-span-3 p-7 md:p-12 flex flex-col justify-center text-left">
          <div className="flex items-center gap-3 mb-4">
            <span className="shyft-special-pill">SPECIAL EVENT</span>
            {event.active && (
              <span className="shyft-live-badge">
                <span className="shyft-live-dot" />
                LIVE
              </span>
            )}
          </div>

          <h3 className="font-display text-3xl md:text-[2.4rem] lg:text-[2.75rem] leading-[1.05] text-white">
            SHYFT Coalition<sup className="shyft-tm">TM</sup>
            <br />
            Launch 300
          </h3>

          <p className="mt-4 text-base md:text-lg shyft-next-class-line" data-testid="special-event-when">
            {event.active ? `In Session Now — Join Live` : localStr}
          </p>

          <p className="mt-3 text-white/70 text-sm md:text-base max-w-xl">
            Our Official Launch hosted by Coach Andre, Coach Chase, and Coach Jo.
            It&rsquo;s time to Shift How You Financially Think.
          </p>

          <div className="mt-7 flex flex-wrap gap-4">
            <a
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shyft-cta-pill inline-flex items-center gap-3 px-9 py-4 text-sm font-semibold tracking-[0.22em]"
              data-testid="special-event-join-button"
            >
              <span className="shyft-dot" />
              JOIN LAUNCH
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Countdown({ targetDate, prefix = "Starts in", onExpired }) {
  const [diff, setDiff] = useState(() =>
    Math.max(0, targetDate.getTime() - Date.now())
  );

  useEffect(() => {
    const tick = () => {
      const remaining = targetDate.getTime() - Date.now();
      if (remaining <= 0) {
        setDiff(0);
        if (typeof onExpired === "function") {
          setTimeout(onExpired, 1500);
        }
        return;
      }
      setDiff(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate, onExpired]);

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  return (
    <div
      className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
      data-testid="countdown"
      aria-label="Countdown to next class"
    >
      <span
        className="font-serif-soft italic text-sm lg:text-base sm:mr-1 text-[#4A3222]"
        style={{ fontStyle: "italic" }}
      >
        {prefix}
      </span>
      <div className="flex items-start justify-center gap-2 sm:gap-3" data-testid="countdown-units">
        <CountUnit value={days} label="Days" testid="count-days" />
        <CountSep />
        <CountUnit value={hours} label="Hrs" testid="count-hrs" />
        <CountSep />
        <CountUnit value={mins} label="Min" testid="count-min" />
        <CountSep />
        <CountUnit value={secs} label="Sec" testid="count-sec" />
      </div>
    </div>
  );
}

function CountUnit({ value, label, testid }) {
  const v = String(Math.max(0, value)).padStart(2, "0");
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="shyft-count-pill">{v}</div>
      <div
        className="text-[0.55rem] sm:text-[0.6rem] tracking-[0.22em] mt-1.5 uppercase"
        style={{ color: "var(--shyft-caramel)" }}
      >
        {label}
      </div>
    </div>
  );
}

function CountSep() {
  return (
    <span
      className="text-base sm:text-lg -mt-3 hidden sm:inline"
      style={{ color: "var(--shyft-caramel)" }}
    >
      :
    </span>
  );
}

function TimePill({ label, active, testid }) {
  return (
    <span
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 px-3 py-[2px] rounded-full text-sm not-italic transition-all duration-300 ${
        active ? "shyft-time-pill-active" : "shyft-time-pill"
      }`}
    >
      {active && <span className="shyft-dot" />}
      {label}
    </span>
  );
}

function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.05 4.91A10 10 0 0 0 12 2a10 10 0 0 0-8.62 14.98L2 22l5.18-1.36A10 10 0 0 0 12 22a10 10 0 0 0 7.05-17.09ZM12 20.18a8.18 8.18 0 0 1-4.17-1.14l-.3-.18-3.07.8.82-3-.2-.32A8.18 8.18 0 1 1 20.18 12 8.19 8.19 0 0 1 12 20.18Zm4.5-6.13c-.25-.13-1.47-.72-1.69-.8-.23-.08-.39-.13-.56.13s-.64.8-.79.96c-.14.17-.29.19-.54.06a6.7 6.7 0 0 1-2-1.23 7.41 7.41 0 0 1-1.37-1.71c-.14-.25 0-.38.11-.5.11-.11.25-.29.38-.43.13-.14.17-.24.25-.4.08-.17.04-.31-.02-.44s-.56-1.36-.77-1.86c-.2-.49-.41-.42-.56-.43h-.48a.93.93 0 0 0-.67.31 2.84 2.84 0 0 0-.88 2.1 4.93 4.93 0 0 0 1.04 2.62 11.31 11.31 0 0 0 4.34 3.85 14.86 14.86 0 0 0 1.45.54 3.48 3.48 0 0 0 1.6.1 2.61 2.61 0 0 0 1.72-1.22 2.16 2.16 0 0 0 .15-1.21c-.07-.11-.23-.18-.48-.31Z" />
    </svg>
  );
}

export default Landing;
